import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { RoutePaths } from 'types/enums';
import { getImageUrl } from 'helpers/getImageUrl';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { getChartRankChange } from '../Charts.helpers';
import * as Styled from '../Charts.styled';
import { ChartEntry, ChartVariants } from '../Charts.types';
import { ChartListDetails } from './ChartListDetails';
import { ChartRankChangeIcon } from './ChartRankChangeIcon';

export type ChartItemProps = {
    chartEntry: ChartEntry;
    index: number;
    isMobile: boolean;
    type: ChartVariants;
};

export const ChartItem: React.FC<ChartItemProps> = ({
    chartEntry,
    index,
    isMobile,
    type,
}) => {
    const rankChange = getChartRankChange(
        chartEntry.currentRank,
        chartEntry.lastRank,
    );

    const toLocalizedPath = useToLocalizedPath();
    const isLabel = type === ChartVariants.Labels;

    const linkPath = isLabel
        ? toLocalizedPath(`/${RoutePaths.Labels}/${chartEntry.slug}`)
        : toLocalizedPath(`/${RoutePaths.Packs}/${chartEntry.slug}`);
    const imageUrl = getImageUrl(chartEntry.artworkUrl, 100);

    const onLinkClick = useCallback(() => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewDetail,
            ctaType: CtaType.PlainText,
            ctaText: chartEntry.name,
            interactionSource: InteractionSource.TrendingCharts,
            ctaDestination: linkPath,
        });
    }, [linkPath, chartEntry]);

    return (
        <Link to={linkPath} onClick={onLinkClick}>
            <Styled.Row>
                <Styled.Index>{index + 1}</Styled.Index>
                {!isMobile && (
                    <ChartRankChangeIcon size="md" rankChange={rankChange} />
                )}
                <Styled.ItemImage
                    src={imageUrl}
                    loading="lazy"
                    fallbackSrc="/assets/fallback/artworkPlaceholder.svg"
                />
                <ChartListDetails chartEntry={chartEntry} type={type} />
                {isMobile && (
                    <ChartRankChangeIcon size="sm" rankChange={rankChange} />
                )}
            </Styled.Row>
        </Link>
    );
};
